import { LTDHUNT_API, endpoints } from './config'

//auth = the user's NextAuth session on app.ltdhunt.com: cookie travels
//via credentials:'include' + host permission (declared in the manifest).
//No custom bridge — when /api/auth/session returns no user, the popup
//offers a "Sign in to LTDHunt" flow instead.
async function request(path, body) {
    const res = await fetch(LTDHUNT_API + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'include',
        headers: body ? { 'Content-Type': 'application/json' } : undefined,
        body: body ? JSON.stringify(body) : undefined,
    })
    if (!res.ok) throw new Error(`ltdhunt_${res.status}`)
    return res.json()
}

//NextAuth public probe: session object when signed in, null when
//signed out, false when the probe itself failed (network/5xx) — callers
//must not treat a failed probe as "signed out"
export async function getLtdhuntSession() {
    try {
        const res = await fetch(LTDHUNT_API + endpoints.session, {
            credentials: 'include',
        })
        if (!res.ok) return false
        const session = await res.json()
        //backend session callback sets user.id=token.sub; email accepted
        //too so the probe survives session-shape changes
        const user = session?.user
        return (user?.id || user?.email) ? session : null
    } catch(e) {
        return false
    }
}

//public endpoint — no session required.
//{ exists, type: 'deal'|'tool'|'none', tool, deal }
export function checkDuplicate(url) {
    return request(endpoints.checkDuplicate, { url })
}

//submit {url} only — Inngest + Perplexity does validation, ProductHunt
//enrichment, extraction, persistence and search indexing server-side.
//202 { pending: true } means the pipeline is still running; callers
//should treat pending as accepted, not an error.
export async function submitDeal({ url }) {
    const res = await fetch(LTDHUNT_API + endpoints.submit, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
    })
    if (res.status === 202) {
        const json = await res.json()
        return { ...json, pending: true }
    }
    if (!res.ok) throw new Error(`ltdhunt_${res.status}`)
    return res.json()
}

//draft review flow: save → user edits → finalize publishes to moderation.
//Kept for the DealForm step; submitDeal is the fast path.
export function saveDealDraft(data, id) {
    return request(endpoints.drafts, { data, ...(id ? { id } : {}) })
}

//public GET — the submit response only carries deal_id; this resolves
//it to slug + tool.slug so the popup can deep-link to the deal page
//instead of the generic /deals list. null on 404/failure.
export async function getDealById(id) {
    try {
        const res = await fetch(`${LTDHUNT_API}${endpoints.deal}${id}`, {
            credentials: 'include',
        })
        return res.ok ? await res.json() : null
    } catch(e) {
        return null
    }
}

export function finalizeDeal(payload) {
    //finalizeSchema: {dealId?, draftId?, toolId?, title, description?,
    //dealUrl?, basePrice?, originalPrice?, productName?, productDescription?,
    //productWebsite?, productLogo?, media?}
    return request(endpoints.finalize, payload)
}

//discussion: the LTDHunt chat is scoped per tool, not per deal — the
//same canonical_comments thread that the site renders in realtime via
//Supabase. Resolving a visited URL to a toolSlug reuses checkDuplicate
//(domain match, marketplace slug, deal→tool); forum pages about a tool
//still resolve to 'none' — the fuzzy title matcher in ./fuzzy covers that.
export async function resolveChatTarget(url) {
    const dup = await checkDuplicate(url)
    if (!dup?.exists) return null

    if (dup.type === 'tool' && dup.tool?.slug)
        return {
            toolSlug: dup.tool.slug,
            toolName: dup.tool.name,
            toolLogo: dup.tool.logo_url,
            toolId: dup.tool.id,
            type: 'tool',
        }

    //the duplicate-check deal row carries tool_name/tool_logo but not
    //tool.slug — fetch the public deal to resolve it
    if (dup.type === 'deal' && dup.deal?.id) {
        const deal = await getDealById(dup.deal.id)
        if (deal?.tool?.slug)
            return {
                toolSlug: deal.tool.slug,
                toolName: deal.tool.name || dup.deal.tool_name,
                toolLogo: deal.tool.logoUrl || dup.deal.tool_logo,
                toolId: deal.tool.id || dup.deal.tool_id,
                type: 'deal',
                dealId: dup.deal.id,
                dealSlug: deal.slug,
            }
    }
    return null
}

//public GET — tool/deal search. The fuzzy forum resolver (./fuzzy) calls
//this per derived title query; returns the tools array only.
export async function searchTools(query, limit = 5) {
    const res = await request(`${endpoints.search}?query=${encodeURIComponent(query)}&limit=${limit}`)
    return res?.tools || []
}

//public GET — deals from tools sharing a topic, scored by shared topic
//count + thermal degrees; falls back to popular deals server-side.
export function getSimilarDeals(toolId, limit = 4) {
    return request(`${endpoints.similarDeals}${encodeURIComponent(toolId)}/similar-deals?limit=${limit}`)
}

//public GET — timeline reads work signed out; includePrivate off so the
//extension never surfaces org-private messages. {toolId, messages,
//hasMore}; messages carry author/reactions/replyToId/editHistory.
export function getChatTimeline(toolSlug, { limit = 50, offset = 0 } = {}) {
    const qs = `tool_slug=${encodeURIComponent(toolSlug)}&limit=${limit}&offset=${offset}`
    return request(`${endpoints.chatTimeline}?${qs}`)
}

//session required — posts into the same thread the site renders.
export function sendChatMessage(toolSlug, content) {
    return request(endpoints.chatSend, { toolSlug, content })
}

//session required — schedules a Novu `reminder-triggered` email at
//remind_at through LTDHunt's pipeline (inserts user_reminders + delayed
//workflow). Existing pending reminder for the same entity is rescheduled
//server-side, so re-clicking moves the date instead of duplicating.
//entity_type 'deal' carries entity_id + original_payload.deal_slug (action
//url becomes /deals/{slug}); 'custom' puts a path in original_payload.url.
export function createReminder({ entityType, entityId, title, note, remindAt, originalPayload }) {
    return request(endpoints.reminders, {
        entity_type: entityType,
        entity_id: entityId || null,
        title,
        note: note || null,
        remind_at: remindAt,
        original_payload: originalPayload || {},
    })
}
